import React, { useRef, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { authHeaders, detailText } from "./auth";
import { useCreditAlert } from "./CreditAlert";
import { resilientFetch } from "./lib/resilientFetch";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;
const MAX_SECONDS = 30;

/**
 * Mic button with imperative API:
 *   - Tap mic → start recording (red pulsing icon, mm:ss timer)
 *   - Parent calls ref.flushIfRecording() → stops recording, uploads to
 *     Whisper, returns transcribed text. Parent can do this from its own
 *     Apply button so user only needs ONE tap to finish.
 *
 *   - onTranscribed(text) is ALSO called whenever a transcription completes
 *     so users can independently stop with the mic if they like.
 *
 * Recording stops by itself at MAX_SECONDS (the backend refuses longer notes).
 */
const VoiceMicButton = forwardRef(function VoiceMicButton(
  { onTranscribed, disabled, className = "" },
  ref
) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [error, setError] = useState("");
  const { triggerCreditAlert, clearCreditAlert } = useCreditAlert();
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startedAtRef = useRef(0);
  const [elapsed, setElapsed] = useState(0);
  const tickRef = useRef(null);

  // Promise that resolves with the transcribed text once a stop+upload finishes.
  const pendingResolveRef = useRef(null);

  useEffect(() => {
    return () => releaseStream();
  }, []);

  const releaseStream = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      /* ignore */
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const upload = async (blob) => {
    setState("uploading");
    setError("");
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voicenote.webm");
      const res = await resilientFetch(`${API}/transcribe`, {
        method: "POST",
        headers: { ...authHeaders() },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        triggerCreditAlert(res, data);
        throw new Error(detailText(data, "Couldn't understand the voice note."));
      }
      const text = (data.text || "").trim();
      if (!text) throw new Error("Empty transcription. Please try again.");
      clearCreditAlert();
      onTranscribed && onTranscribed(text);
      if (pendingResolveRef.current) pendingResolveRef.current(text);
      pendingResolveRef.current = null;
      setState("idle");
      return text;
    } catch (e) {
      setError(e.message || "Voice transcription failed.");
      if (pendingResolveRef.current) pendingResolveRef.current(null);
      pendingResolveRef.current = null;
      setState("idle");
      return null;
    }
  };

  const start = async () => {
    setError("");
    if (!navigator.mediaDevices || typeof MediaRecorder === "undefined") {
      setError("Voice recording isn't supported on this device.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // 32 kbps is plenty for speech and keeps a 30 s note around 120 KB.
      let mr;
      try {
        mr = new MediaRecorder(stream, { audioBitsPerSecond: 32000 });
      } catch {
        mr = new MediaRecorder(stream);
      }
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: chunksRef.current[0]?.type || "audio/webm",
        });
        releaseStream();
        if (blob.size > 0) upload(blob);
        else {
          if (pendingResolveRef.current) pendingResolveRef.current(null);
          pendingResolveRef.current = null;
          setState("idle");
        }
      };
      mr.start();
      setState("recording");
      startedAtRef.current = Date.now();
      setElapsed(0);
      tickRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startedAtRef.current) / 1000);
        setElapsed(Math.min(secs, MAX_SECONDS));
        if (secs >= MAX_SECONDS && mr.state === "recording") mr.stop();  // auto-stop + upload
      }, 250);
    } catch {
      setError("Mic permission denied or unavailable.");
      setState("idle");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  // Imperative API for the parent
  useImperativeHandle(ref, () => ({
    isRecording: () => state === "recording",
    isBusy: () => state === "uploading",
    // Stop + upload + resolve with transcribed text (or null on failure).
    // If not recording, resolves immediately with null.
    flushIfRecording: () => {
      if (state !== "recording") return Promise.resolve(null);
      return new Promise((resolve) => {
        pendingResolveRef.current = resolve;
        stopRecording();
      });
    },
  }));

  const handleClick = () => {
    if (disabled || state === "uploading") return;
    if (state === "recording") stopRecording();
    else start();
  };

  return (
    <div className="flex flex-col items-end">
      <button
        type="button"
        data-testid="voice-mic-btn"
        onClick={handleClick}
        disabled={disabled || state === "uploading"}
        title={
          state === "recording"
            ? "Recording… tap Apply when done (or tap mic to stop)"
            : state === "uploading"
              ? "Transcribing…"
              : "Tap to record voice note in any language"
        }
        className={[
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition",
          state === "recording"
            ? "animate-pulse bg-red-500 text-white shadow-lg ring-2 ring-red-300/50"
            : state === "uploading"
              ? "bg-heda-gold/80 text-white"
              : "bg-white/10 text-white hover:bg-white/20",
          disabled ? "cursor-not-allowed opacity-40" : "",
          className,
        ].join(" ")}
      >
        {state === "uploading" ? (
          <Loader2 size={14} className="animate-spin" />
        ) : state === "recording" ? (
          <Square size={12} fill="currentColor" />
        ) : (
          <Mic size={14} />
        )}
      </button>
      {state === "recording" && (
        <span className="mt-1 text-[10px] font-bold tabular-nums text-red-300">
          {String(Math.floor(elapsed / 60)).padStart(1, "0")}:
          {String(elapsed % 60).padStart(2, "0")}
        </span>
      )}
      {error && (
        <span
          data-testid="voice-mic-error"
          className="mt-1 max-w-[200px] text-right text-[10.5px] leading-tight text-red-300"
        >
          {error}
        </span>
      )}
    </div>
  );
});

export default VoiceMicButton;
